
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";

export interface UserProfile {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: 'admin' | 'super_admin';
  is_active: boolean;
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);

  const fetchProfile = async (userId: string, userEmail?: string): Promise<UserProfile | null> => {
    try {
      console.log('Fetching profile for user:', userId);
      
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching profile:", error);
        throw new Error("Failed to load user profile");
      }
      
      if (!data) {
        console.log('No profile found, creating new profile...');
        
        const { data: newProfile, error: createError } = await supabase
          .from("profiles")
          .insert({
            id: userId,
            email: userEmail || '',
            first_name: '',
            last_name: '',
            role: 'admin',
            is_active: true
          })
          .select()
          .single();
          
        if (createError) {
          console.error('Error creating profile:', createError);
          throw new Error("Failed to create user profile");
        }
        
        console.log('Created new profile:', newProfile);
        return newProfile;
      }
      
      console.log('Profile fetched successfully:', data);
      return data;
    } catch (error) {
      console.error("Profile fetch error:", error);
      throw error;
    }
  };

  useEffect(() => {
    let mounted = true;
    let sessionRestored = false;

    // Auth state change handler
    const handleAuthState = async (event: string | null, newSession: Session | null) => {
      if (!mounted) return;
      
      console.log('Auth state change:', { event, userEmail: newSession?.user?.email || 'No user' });
      
      // Always update session and user state immediately
      setSession(newSession);
      setUser(newSession?.user || null);
      
      if (!newSession?.user) {
        // No user - clear everything and stop loading
        setProfile(null);
        setProfileError(null);
        setLoading(false);
        sessionRestored = true;
        return;
      }

      // User exists - fetch profile with session recovery
      try {
        const userProfile = await fetchProfile(newSession.user.id, newSession.user.email);
        if (mounted) {
          setProfile(userProfile);
          setProfileError(null);
        }
      } catch (error: any) {
        console.error('Profile fetch failed:', error);
        if (mounted) {
          setProfile(null);
          setProfileError(error.message);
        }
      } finally {
        if (mounted) {
          setLoading(false);
          sessionRestored = true;
        }
      }
    };

    // Set up auth state change listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Use setTimeout to prevent potential deadlocks
      setTimeout(() => {
        handleAuthState(event, session);
      }, 0);
    });

    // Then check for existing session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting initial session:', error);
          if (mounted) {
            setLoading(false);
          }
          return;
        }

        console.log('Initial session check:', session?.user?.email || 'No session');
        
        // Only handle initial session if not already processed by listener
        if (!sessionRestored && mounted) {
          await handleAuthState('INITIAL_SESSION', session);
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    // Small delay to ensure DOM is ready
    const initTimer = setTimeout(initializeAuth, 100);

    return () => {
      mounted = false;
      clearTimeout(initTimer);
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      setLoading(true);
      await supabase.auth.signOut();
      // Clear all state immediately
      setSession(null);
      setUser(null);
      setProfile(null);
      setProfileError(null);
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = () => {
    return profile?.role === 'admin' || profile?.role === 'super_admin';
  };

  const isSuperAdmin = () => {
    return profile?.role === 'super_admin';
  };

  return {
    user,
    session,
    profile,
    loading,
    profileError,
    signOut,
    isAdmin,
    isSuperAdmin,
  };
};
